import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import GraphBuilder, { type GraphPoint } from '@routes/Panorama/data/GraphBuilder'
import UnionFind from '@routes/Panorama/data/UnionFind'
import { useLayoutTransformed } from '@state/layout/queries'
import { type PanoramaImage, usePanorama } from '@state/panorama'
import useScanContextAwareHttpClient from '@utils/ScanContextAwareHttpClient'

import type { TransformedShelf } from '@state/shelf/types'

interface Point {
  x: number
  y: number
}

export interface Edge {
  from: PanoramaImage
  to: PanoramaImage
}

interface WeightedEdge extends Edge {
  weight: number
}

export interface PanoramaNetwork {
  edges: Edge[]
  graph: GraphPoint[]
}

export interface AABB {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

const EPSILON = 1e-5

/**
 * Rotates a point around (0,0) by a given angle in radians.
 */
function rotatePoint(p: Point, angle: number): Point {
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  return {
    x: p.x * cos - p.y * sin,
    y: p.x * sin + p.y * cos,
  }
}

/**
 * The Liang-Barsky Line Clipping Algorithm.
 * Returns true if the segment p1-p2 intersects the Axis-Aligned Bounding Box.
 */
export function segmentIntersectsAABB(p1: Point, p2: Point, box: AABB): boolean {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y

  const borders = [-dx, dx, -dy, dy]
  const distancesToBorder = [
    p1.x - box.minX,
    box.maxX - p1.x,
    p1.y - box.minY,
    box.maxY - p1.y,
  ]

  let t0 = 0.0 // Entry time
  let t1 = 1.0 // Exit time

  for (let i = 0; i < 4; i++) {
    const border = borders[i]!
    const distanceToBorder = distancesToBorder[i]!

    if (Math.abs(border) < EPSILON) {
      // Line is parallel to this border
      if (distanceToBorder < 0) return false // Line is outside the parallel border
    } else {
      const t = distanceToBorder / border
      if (border < 0) {
        // Line entering the slab
        if (t > t1) return false // Entry is after exit (miss)
        if (t > t0) t0 = t // Push entry forward
      } else {
        // Line exiting the slab
        if (t < t0) return false // Exit is before entry (miss)
        if (t < t1) t1 = t // Pull exit backward
      }
    }
  }

  return t0 <= t1
}

/**
 * Checks intersection with an Arbitrarily Rotated Rectangle.
 * Logic: Transform the ray into the rectangle's local coordinate space,
 * then run Liang-Barsky against the unrotated dimensions.
 */
export function segmentIntersectsRotatedShelf(p1: Point, p2: Point, shelf: TransformedShelf): boolean {
  // 1. Translate points relative to the rectangle center
  const relP1 = { x: p1.x - shelf.center.x, y: p1.y - shelf.center.y }
  const relP2 = { x: p2.x - shelf.center.x, y: p2.y - shelf.center.y }

  // 2. Rotate points by the NEGATIVE rectangle rotation
  // This aligns the world with the rectangle's axes
  const localP1 = rotatePoint(relP1, shelf.rotationRad)
  const localP2 = rotatePoint(relP2, shelf.rotationRad)

  // 3. Define the Local AABB (centered at 0,0)
  const halfW = shelf.width / 2
  const halfH = shelf.height / 2

  const localBox: AABB = {
    minX: -halfW,
    maxX:  halfW,
    minY: -halfH,
    maxY:  halfH,
  }

  // 4. Run standard AABB check
  return segmentIntersectsAABB(localP1, localP2, localBox)
}

// Penalty multiplier for edges that are blocked by obstacles.
// This ensures Kruskal's algorithm strongly prefers visible edges,
// but can still use blocked edges as bridges when necessary.
const BLOCKED_EDGE_PENALTY = 10_000_000

/**
 * Builds a visibility network using a single-pass Kruskal's algorithm approach.
 *
 * All unique point pairs are assigned a weight:
 * - Visible pairs: weight = distance
 * - Blocked pairs (by shelves): weight = BLOCKED_EDGE_PENALTY * distance
 *
 * Kruskal's algorithm then selects edges in weight order, naturally preferring
 * visible connections while still ensuring full connectivity via bridge edges when needed.
 */
export function buildVisibilityNetwork(panoramaImages: PanoramaImage[], shelves: TransformedShelf[]): Edge[] {
  const weightedEdges: WeightedEdge[] = []

  // Generate all unique pairs with weights
  for (let i = 0; i < panoramaImages.length; i++) {
    for (let j = i + 1; j < panoramaImages.length; j++) {
      const pA = panoramaImages[i]!
      const pB = panoramaImages[j]!

      const distSq = (pA.x - pB.x) ** 2 + (pA.y - pB.y) ** 2

      // Check if the edge is blocked by any shelf
      let isBlocked = false
      for (const shelf of shelves) {
        if (segmentIntersectsRotatedShelf(pA, pB, shelf)) {
          isBlocked = true
          break
        }
      }

      const weight = isBlocked ? BLOCKED_EDGE_PENALTY * distSq : distSq
      weightedEdges.push({ from: pA, to: pB, weight })
    }
  }

  // Sort by weight (ascending) - visible short edges first, blocked edges last
  weightedEdges.sort((a, b) => a.weight - b.weight)

  // Kruskal's Algorithm: Build MST-like structure
  // This naturally handles both visibility connections and bridge edges
  const uf = new UnionFind(panoramaImages.map((p) => p.id))
  const edges: Edge[] = []

  for (const edge of weightedEdges) {
    // if start and end of the edge are part of the same component, skip it
    if (uf.find(edge.from.id) === uf.find(edge.to.id)) continue

    uf.union(edge.from.id, edge.to.id)
    edges.push({ from: edge.from, to: edge.to })
  }

  return edges
}

function createNetwork(panoramaImages: PanoramaImage[], shelves: TransformedShelf[]): PanoramaNetwork {
  const graphBuilder = new GraphBuilder()
  const edges = buildVisibilityNetwork(panoramaImages, shelves)

  edges.forEach(({ from, to }) => graphBuilder.addBidirectionalConnection(from, to))

  return { edges, graph: graphBuilder.toArray() }
}

export function usePanoramaNetwork() {
  const { data: panoramaImages } = usePanorama()
  const { data: layout } = useLayoutTransformed({ complete: false })

  const { storeId, scanId } = useScanContextAwareHttpClient()

  return useQuery<PanoramaNetwork | undefined>({
    queryKey: ['panorama-network', storeId, scanId],
    queryFn: () => {
      if (layout === undefined || panoramaImages === undefined) {
        return undefined
      }

      return createNetwork(panoramaImages, layout.shelves)
    },
    staleTime: ms('1d'),
    enabled: layout !== undefined && panoramaImages !== undefined,
  })
}

export function useGraphNode(currentPanoramaImage?: PanoramaImage) {
  const { data: panoramaNetwork } = usePanoramaNetwork()
  if (!currentPanoramaImage || !panoramaNetwork) return undefined

  const currentGraphNode = panoramaNetwork.graph.find(node => node.point === currentPanoramaImage)
  if (!currentGraphNode) return undefined

  return currentGraphNode
}